import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import getUserLocale from 'get-user-locale';

import http from '../../utils/services/http';
import globalLogger from '../../logger';
import { METHODS } from '../../../common/utils/http/httpConstants';
import { APP_QUERY_PARAMS } from '../../../common/utils/urlConstants';
import { DEFAULT_LOCALE, type SupportedLocale } from '../../../common/cms/cmsLocaleConstants';
import { getOptionalLocaleForCMS } from '../../../common/cms/localeValidationUtils';
import { DEFAULT_RELEASED_LOCALE_CODES } from '../../../common/experiments/localeExperimentConstants';
import {
    getAppLanguageOptions,
    type LocaleOption,
} from '../../user/account/accountModal/localisation/localizationConstants';

const logger = globalLogger.createChannel('auth.locale');

// Cache for released auth locales to avoid multiple API calls
let releasedAuthLocalesPromise: Promise<string[]> | null = null;

/**
 * Detects the browser locale and normalizes it to a supported CMS locale.
 * Returns null when the browser locale cannot be resolved to a supported auth locale.
 */
export const getNormalizedBrowserLocale = (): SupportedLocale | null => {
    try {
        return getOptionalLocaleForCMS(getUserLocale());
    } catch (error) {
        logger.warn('Failed to detect language from browser locale', error);
        return null;
    }
};

/**
 * Attempts to retrieve the device language code from Capacitor's Device API.
 * Only works on native mobile platforms.
 * @returns The language code if available, null otherwise
 */
const getCapacitorDeviceLanguage = async (): Promise<string | null> => {
    if (!Capacitor.isNativePlatform()) return null;

    try {
        // Returns the BPC-47 language tag (e.g. "en-US"), which is more specific than the two-letter code from getLanguageCode()
        const { value: languageTag } = await Device.getLanguageTag();
        if (languageTag) return languageTag;
    } catch (error) {
        logger.warn('Failed to detect language tag from Capacitor Device API', error);
    }

    try {
        const { value } = await Device.getLanguageCode();
        return value || null;
    } catch (error) {
        logger.warn('Failed to detect language code from Capacitor Device API', error);
        return null;
    }
};

/**
 * Detects the user's preferred language from their device or browser settings.
 * Prioritizes Capacitor device language (mobile) over browser language detection.
 * @returns The detected language code, or null if detection fails
 */
export const getPreferredDeviceOrBrowserLanguage = async (): Promise<string | null> => {
    const capacitorLanguage = await getCapacitorDeviceLanguage();
    if (capacitorLanguage) return capacitorLanguage;

    return getNormalizedBrowserLocale();
};

/**
 * Fetches the list of released locale codes from the server.
 * Falls back to default released locales if the API call fails.
 * @returns Array of normalized locale codes
 */
const fetchReleasedAuthLocales = async (): Promise<string[]> => {
    try {
        const response = await http({ method: METHODS.GET, url: 'locales' });
        const localeCodes: string[] = response.data?.localeCodes || [];

        // Normalize locale codes and filter out unsupported ones
        const normalized = localeCodes
            .map((code: string) => getOptionalLocaleForCMS(code))
            .filter((code): code is SupportedLocale => !!code);

        // Merge with defaults and deduplicate
        return [...new Set([...DEFAULT_RELEASED_LOCALE_CODES, ...normalized])];
    } catch (error) {
        logger.warn('Failed to fetch released locales for auth localization, falling back to defaults', error);
        return [...DEFAULT_RELEASED_LOCALE_CODES];
    }
};

/**
 * Retrieves the list of released locale codes available for authentication pages.
 * Uses memoization to ensure the API is only called once.
 * @returns Promise resolving to array of locale codes
 */
export const getReleasedAuthLocales = (): Promise<string[]> => {
    if (!releasedAuthLocalesPromise) {
        releasedAuthLocalesPromise = fetchReleasedAuthLocales();
    }

    return releasedAuthLocalesPromise;
};

/**
 * Resolves the optimal locale for authentication pages using a priority fallback strategy:
 * 1. URL query parameter locale (highest priority)
 * 2. Saved locale from localStorage
 * 3. Device/browser language preference
 * 4. Default locale (fallback)
 *
 * Only returns locales that are currently released.
 * @param options - Object containing urlLocale and savedLocale
 * @returns Promise resolving to the selected locale code
 */
export const getAuthLocale = async ({
    urlLocale,
    savedLocale,
}: {
    urlLocale?: string | null;
    savedLocale?: string | null;
}): Promise<string> => {
    const [releasedLocales, browserLocale] = await Promise.all([
        getReleasedAuthLocales(),
        getPreferredDeviceOrBrowserLanguage(),
    ]);
    const released = new Set(releasedLocales);

    // Attempt each locale source in priority order
    for (const locale of [urlLocale, savedLocale, browserLocale]) {
        const supported = getOptionalLocaleForCMS(locale);
        if (supported && released.has(supported)) return supported;
    }

    return DEFAULT_LOCALE;
};

/**
 * Retrieves the list of locale options available for display in the auth UI.
 * Filters the full locale options list to only include released locales.
 * @returns Promise resolving to array of LocaleOption objects
 */
export const getReleasedAuthLocaleOptions = async (): Promise<LocaleOption[]> => {
    const released = new Set(await getReleasedAuthLocales());
    return getAppLanguageOptions(() => true).filter((option) => released.has(option.id));
};

/**
 * Extracts the locale code from URL query parameters.
 * @param query - Query parameters object
 * @returns The locale code if present and valid, null otherwise
 */
export const getAuthLocaleQuery = (query: Record<string, unknown> | undefined): string | null => {
    const locale = query?.[APP_QUERY_PARAMS.APP_LOCALE];
    return typeof locale === 'string' ? locale : null;
};
